import React, { useState } from 'react';
import { toast } from 'sonner';
import {
  API, Card, DataTable, Empty, Spin, useApi, authHeaders, fmtWhen, CustomerLink,
} from './SecurityShared';
import { RefreshCw, ShieldOff, ShieldCheck, LogOut, Gauge } from 'lucide-react';

/** Login risk — failed attempts, new country/IP, shared IPs, blocked retries. */
export function LoginsTab({ token, onOpenCustomer }) {
  const [hours, setHours] = useState(168);
  const { data, loading, reload } = useApi(token, `/api/admin/security/login-risk?hours=${hours}`, [hours]);
  const Block = ({ title, rows, cols, testid, rowTestid }) => (
    <Card title={title} testid={testid}>
      {loading ? <Spin /> : !rows?.length ? <Empty>Nothing in this window</Empty> : (
        <DataTable rows={rows} cols={cols} rowTestid={rowTestid} />
      )}
    </Card>
  );
  return (
    <>
      <div className="flex items-center gap-2 mb-3">
        <select value={hours} onChange={(e) => setHours(Number(e.target.value))} data-testid="sec-logins-window"
          className="px-2 py-1.5 text-xs font-semibold rounded-lg border border-slate-300 bg-white">
          <option value={24}>Last 24 h</option>
          <option value={168}>Last 7 days</option>
          <option value={720}>Last 30 days</option>
        </select>
        <button onClick={reload} data-testid="sec-logins-refresh" className="p-1.5 rounded-lg border border-slate-300">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <Block title="New country / new IP for an existing customer" testid="sec-newgeo-card"
        rows={data?.new_geo} rowTestid={(r, i) => `sec-newgeo-row-${i}`}
        cols={[
          { key: 'cust', label: 'Customer', primary: true,
            render: (r) => <CustomerLink email={r.email} userId={r.user_id} onOpenCustomer={onOpenCustomer} /> },
          { key: 'countries', label: 'New countries', tdClass: 'font-semibold text-amber-700',
            render: (r) => <span className="font-semibold text-amber-700">{(r.new_countries || []).join(', ') || '—'}</span> },
          { key: 'ips', label: 'New IPs', tdClass: 'font-mono text-slate-600', full: true,
            render: (r) => <span className="font-mono break-all">{(r.new_ips || []).join(', ') || '—'}</span> },
          { key: 'last', label: 'Last login', tdClass: 'text-slate-500 whitespace-nowrap', render: (r) => fmtWhen(r.last) },
        ]} />

      <Block title="One IP → several accounts" testid="sec-sharedip-card"
        rows={data?.shared_ips} rowTestid={(r, i) => `sec-sharedip-row-${i}`}
        cols={[
          { key: 'ip', label: 'IP', primary: true, tdClass: 'font-mono text-slate-800',
            render: (r) => <span className="font-mono">{r.ip}</span> },
          { key: 'accounts', label: 'Accounts', tdClass: 'font-bold text-rose-700',
            render: (r) => <span className="font-bold text-rose-700">{r.accounts}</span> },
          { key: 'hits', label: 'Attempts', tdClass: 'tabular-nums', render: (r) => r.hits },
          { key: 'last', label: 'Last', tdClass: 'text-slate-500 whitespace-nowrap', render: (r) => fmtWhen(r.last) },
          { key: 'emails', label: 'E-mails', full: true, tdClass: 'text-slate-600 max-w-[320px] truncate',
            render: (r) => (
              <span title={(r.emails || []).join(', ')} className="break-words">
                {(r.emails || []).filter(Boolean).join(', ')}
              </span>) },
        ]} />

      <Block title="Second factor: who got past the password" testid="sec-mfa-card"
        rows={data?.mfa_events} rowTestid={(r, i) => `sec-mfa-row-${i}`}
        cols={[
          { key: 'who', label: 'Account', primary: true,
            render: (r) => <span className="font-semibold text-slate-800 break-all">{r.actor_email || '—'}</span> },
          { key: 'event', label: 'Stage', primary: true,
            render: (r) => {
              const label = {
                'login.mfa_challenge_issued': 'password OK → code asked',
                'login.mfa_setup_required': 'password OK → enrolment forced',
                'mfa.verify_failed': 'wrong code',
                'mfa.verify_success': 'signed in',
              }[r.event] || r.event;
              const tone = r.event === 'mfa.verify_success' ? 'bg-emerald-100 text-emerald-700'
                : r.event === 'mfa.verify_failed' ? 'bg-rose-100 text-rose-700'
                  : 'bg-amber-100 text-amber-800';
              return <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${tone}`}>{label}</span>;
            } },
          { key: 'when', label: 'When', tdClass: 'text-slate-500 whitespace-nowrap', render: (r) => fmtWhen(r.ts) },
          { key: 'ip', label: 'IP', tdClass: 'font-mono text-slate-600 break-all', render: (r) => r.ip || '—' },
        ]} />

      <Block title="Failed login attempts" testid="sec-failed-card"
        rows={data?.failed} rowTestid={(r, i) => `sec-failed-row-${i}`}
        cols={[
          { key: 'cust', label: 'Customer', primary: true,
            render: (r) => <CustomerLink email={r.user_email} userId={r.user_id} onOpenCustomer={onOpenCustomer} /> },
          { key: 'reason', label: 'Reason', primary: true,
            render: (r) => <span className="text-rose-700 text-[11px] font-bold font-mono">{r.reason || '—'}</span> },
          { key: 'when', label: 'When', tdClass: 'text-slate-500 whitespace-nowrap', render: (r) => fmtWhen(r.created_at) },
          { key: 'ip', label: 'IP', tdClass: 'font-mono text-slate-600', render: (r) => r.ip || '—' },
          { key: 'country', label: 'Country', render: (r) => r.country || '—' },
          { key: 'client', label: 'Client', full: true, tdClass: 'text-slate-500 max-w-[260px] truncate',
            render: (r) => (
              <span title={r.user_agent || ''}>
                {[r.browser, r.os].filter(Boolean).join(' · ') || r.user_agent || '—'}
              </span>) },
        ]} />

      <Block title="Login attempts by blocked accounts" testid="sec-blockedtry-card"
        rows={data?.blocked_attempts} rowTestid={(r, i) => `sec-blockedtry-row-${i}`}
        cols={[
          { key: 'cust', label: 'Customer', primary: true,
            render: (r) => <CustomerLink email={r.user_email} userId={r.user_id} onOpenCustomer={onOpenCustomer} /> },
          { key: 'when', label: 'When', tdClass: 'text-slate-500 whitespace-nowrap', render: (r) => fmtWhen(r.created_at) },
          { key: 'ip', label: 'IP', tdClass: 'font-mono text-slate-600', render: (r) => r.ip || '—' },
          { key: 'country', label: 'Country', render: (r) => r.country || '—' },
          { key: 'success', label: 'Success', render: (r) => (r.success ? 'yes' : 'no') },
        ]} />
    </>
  );
}

/** Access: blocked accounts, Trusted list, customers that hit a limit. */
export function AccessTab({ token, onOpenCustomer }) {
  const [tab, setTab] = useState('blocked');
  const paths = {
    blocked: '/api/admin/customers/filter?role=access_denied&limit=200',
    inactive: '/api/admin/customers/filter?is_active=false&limit=200',
    trusted: '/api/admin/customers/filter?trusted=true&limit=200',
  };
  const { data: list, loading, reload } = useApi(token, paths[tab] || paths.blocked, [tab]);
  const { data: hits, loading: hitsLoading, reload: reloadHits } = useApi(token, '/api/admin/security/rate-limit-hits?hours=168');

  const act = async (u, action) => {
    const confirmText = {
      block: `Deny access for ${u.email}?`,
      unblock: `Restore access for ${u.email}?`,
      trust: `Mark ${u.email} as Trusted?`,
      untrust: `Remove Trusted from ${u.email}?`,
      revoke: `Revoke all sessions of ${u.email}? They will have to log in again.`,
    }[action];
    if (!window.confirm(confirmText)) return;
    let url = `${API}/api/admin/customers/${u.id}/profile`;
    let opts = { method: 'PUT', headers: { ...authHeaders(token), 'Content-Type': 'application/json' } };
    if (action === 'block') opts.body = JSON.stringify({ role: 'access_denied' });
    if (action === 'unblock') opts.body = JSON.stringify({ role: 'user', is_active: true });
    if (action === 'trust' || action === 'untrust') {
      url = `${API}/api/admin/customers/${u.id}/trusted`;
      opts.body = JSON.stringify({ trusted: action === 'trust' });
    }
    if (action === 'revoke') {
      url = `${API}/api/admin/security/users/${u.id}/revoke-sessions`;
      opts = { method: 'POST', headers: authHeaders(token) };
    }
    const r = await fetch(url, opts);
    const j = await r.json().catch(() => ({}));
    if (!r.ok) { toast.error(j.detail || 'Failed'); return; }
    toast.success('Done — the customer card shows the same state');
    reload();
  };

  const TABS = [
    { id: 'blocked', label: 'Access denied' },
    { id: 'inactive', label: 'Deactivated' },
    { id: 'trusted', label: 'Trusted' },
  ];

  return (
    <>
      <Card title="Customer access" testid="sec-access-card"
        hint="Uses the same roles and Trusted flag as the customer card — changes here show up there instantly."
        right={(
          <>
            <div className="inline-flex bg-slate-100 rounded-lg p-0.5">
              {TABS.map((t) => (
                <button key={t.id} onClick={() => setTab(t.id)} data-testid={`sec-access-tab-${t.id}`}
                  className={`px-2.5 py-1 rounded text-[11px] font-bold ${tab === t.id ? 'bg-white shadow text-slate-800' : 'text-slate-500'}`}>
                  {t.label}
                </button>
              ))}
            </div>
            <button onClick={reload} data-testid="sec-access-refresh" className="p-1.5 rounded-lg border border-slate-300">
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </>
        )}>
        {loading ? <Spin /> : !list?.length ? <Empty testid="sec-access-empty">No customers in this list</Empty> : (
          <DataTable testid="sec-access-table" rows={list}
            rowKey={(u) => u.id} rowTestid={(u, i) => `sec-access-row-${i}`}
            cols={[
              { key: 'cust', label: 'Customer', primary: true,
                render: (u, i) => (
                  <>
                    <CustomerLink email={u.email} userId={u.id} onOpenCustomer={onOpenCustomer} testid={`sec-access-customer-${i}`} />
                    <div className="text-[11px] text-slate-500">{[u.first_name, u.surname].filter(Boolean).join(' ')}</div>
                  </>) },
              { key: 'account', label: 'Account', tdClass: 'font-mono text-slate-500', render: (u) => `#${u.account_id || '—'}` },
              { key: 'role', label: 'Role', render: (u) => u.role },
              { key: 'active', label: 'Active', render: (u) => (u.is_active ? 'yes' : 'no') },
              { key: 'trusted', label: 'Trusted', render: (u) => (u.trusted ? 'yes' : '—') },
              { key: 'reg', label: 'Registered', tdClass: 'text-slate-500 whitespace-nowrap', render: (u) => fmtWhen(u.registration_date) },
              { key: 'actions', label: 'Actions', right: true, full: true,
                render: (u, i) => (
                  <div className="flex flex-wrap gap-1 md:justify-end text-xs">
                    {u.role === 'access_denied' || !u.is_active ? (
                      <button onClick={() => act(u, 'unblock')} data-testid={`sec-access-unblock-${i}`}
                        className="px-2 py-1 rounded bg-emerald-600 text-white font-bold flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Unblock</button>
                    ) : (
                      <button onClick={() => act(u, 'block')} data-testid={`sec-access-block-${i}`}
                        className="px-2 py-1 rounded bg-rose-600 text-white font-bold flex items-center gap-1"><ShieldOff className="w-3 h-3" /> Block</button>
                    )}
                    <button onClick={() => act(u, u.trusted ? 'untrust' : 'trust')} data-testid={`sec-access-trust-${i}`}
                      className="px-2 py-1 rounded border border-slate-300 bg-white font-bold">{u.trusted ? 'Untrust' : 'Trust'}</button>
                    <button onClick={() => act(u, 'revoke')} data-testid={`sec-access-revoke-${i}`}
                      className="px-2 py-1 rounded border border-slate-300 bg-white font-bold flex items-center gap-1"><LogOut className="w-3 h-3" /> Sessions</button>
                  </div>) },
            ]} />
        )}
      </Card>

      <Card title="Customers who hit a limit (7 days)" testid="sec-ratelimit-card"
        hint="Every 429 is recorded. If a regular customer shows up here, switch Trusted on in their card."
        right={<button onClick={reloadHits} className="p-1.5 rounded-lg border border-slate-300" data-testid="sec-ratelimit-refresh">
          <RefreshCw className={`w-3.5 h-3.5 ${hitsLoading ? 'animate-spin' : ''}`} /></button>}>
        {hitsLoading ? <Spin /> : !hits?.per_user?.length ? <Empty testid="sec-ratelimit-empty">Nobody hit a limit</Empty> : (
          <DataTable testid="sec-ratelimit-table" rows={hits.per_user}
            rowTestid={(r, i) => `sec-ratelimit-row-${i}`}
            cols={[
              { key: 'cust', label: 'Customer', primary: true,
                render: (r) => <CustomerLink email={r.email} userId={r.user_id} onOpenCustomer={onOpenCustomer} /> },
              { key: 'hits', label: 'Hits',
                render: (r) => <span className="font-bold text-amber-700 flex items-center gap-1"><Gauge className="w-3 h-3" /> {r.hits}</span> },
              { key: 'trusted', label: 'Trusted', render: (r) => (r.trusted ? 'yes' : '—') },
              { key: 'last', label: 'Last', tdClass: 'text-slate-500 whitespace-nowrap', render: (r) => fmtWhen(r.last) },
            ]} />
        )}
      </Card>
    </>
  );
}

/** Admin accounts — MFA, last login, revoke sessions. */
export function AdminsTab({ token }) {
  const { data, loading, reload } = useApi(token, '/api/admin/security/admins');
  const revoke = async (u) => {
    if (!window.confirm(`Revoke all sessions of ${u.email}?`)) return;
    const r = await fetch(`${API}/api/admin/security/users/${u.id}/revoke-sessions`,
      { method: 'POST', headers: authHeaders(token) });
    if (!r.ok) { toast.error('Failed'); return; }
    toast.success('Sessions revoked'); reload();
  };
  return (
    <Card title="Administrators" testid="sec-admins-card"
      hint="Who can enter the admin panel, their MFA state, last login and IP."
      right={<button onClick={reload} className="p-1.5 rounded-lg border border-slate-300" data-testid="sec-admins-refresh">
        <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /></button>}>
      {loading ? <Spin /> : !data?.rows?.length ? <Empty>No admin accounts</Empty> : (
        <DataTable testid="sec-admins-table" rows={data.rows}
          rowKey={(u) => u.id} rowTestid={(u, i) => `sec-admin-row-${i}`}
          cols={[
            { key: 'email', label: 'Admin', primary: true,
              render: (u) => <span className="font-semibold text-slate-800 break-all">{u.email}</span> },
            { key: 'mfa', label: 'MFA', primary: true,
              render: (u) => (
                <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${u.mfa_enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                  MFA {u.mfa_enabled ? 'ON' : 'OFF'}
                </span>) },
            { key: 'tier', label: 'Tier', render: (u) => u.admin_tier || '—' },
            { key: 'last', label: 'Last login', tdClass: 'text-slate-500 whitespace-nowrap', render: (u) => fmtWhen(u.last_login?.created_at) },
            { key: 'ip', label: 'IP / country', tdClass: 'font-mono text-slate-600',
              render: (u) => `${u.last_login?.ip || u.last_ip || '—'}${u.last_login?.country ? ` · ${u.last_login.country}` : ''}` },
            { key: 'failed', label: 'Failed 24 h',
              render: (u) => <span className={u.failed_24h ? 'text-rose-700 font-bold' : 'text-slate-400 font-bold'}>{u.failed_24h || 0}</span> },
            { key: 'action', label: 'Action', right: true, full: true,
              render: (u, i) => (
                <button onClick={() => revoke(u)} data-testid={`sec-admin-revoke-${i}`}
                  className="px-2 py-1 rounded border border-slate-300 bg-white text-xs font-bold inline-flex items-center gap-1 md:ml-auto"><LogOut className="w-3 h-3" /> Revoke</button>) },
          ]} />
      )}
    </Card>
  );
}
